import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ControllerAlert,
  ControllerMenuItem,
  ControllerModal,
  ControllerSelect,
  ControllerTextField,
  DialogActions,
  DialogContent,
  Stack,
} from '@bymason-private/legacy-pattern-components';
import { DangrousNotificationType } from '@bymason/controller-api-hooks';
import { commandDescription } from './logic';

interface ActionConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (command: DangrousNotificationType) => void;
}

export function ActionConfirmationModal({ onSubmit, onClose, open }: ActionConfirmationModalProps) {
  const [command, setCommand] = useState<DangrousNotificationType>(DangrousNotificationType.Reboot);
  const [confirmation, setConfirmation] = useState('');

  const resetFormState = () => {
    setCommand(DangrousNotificationType.Reboot);
    setConfirmation('');
  };

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (command) {
      onSubmit(command);
      handleClose();
    }
  }, [command, handleClose, onSubmit]);

  const handleCommandChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setCommand(value as DangrousNotificationType);
  };

  const handleConfirmationChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setConfirmation(value);
  };

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open]);

  const isValidConfirm = useMemo(() => {
    return confirmation.toLocaleLowerCase() === command || command === DangrousNotificationType.Reboot;
  }, [command, confirmation]);

  const enableSubmit = command && isValidConfirm;

  return (
    <ControllerModal onClose={onClose} open={open} title='More commands'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <ControllerSelect
            label='Command'
            name='command'
            value={command}
            autoFocus
            onChange={handleCommandChange}
            required
            fullWidth
          >
            {Object.values(DangrousNotificationType).map((name) => (
              <ControllerMenuItem key={name} value={name}>
                {name.toLocaleUpperCase()}
              </ControllerMenuItem>
            ))}
          </ControllerSelect>
          <ControllerAlert
            sx={{ width: '100%' }}
            severity={command === DangrousNotificationType.Reboot ? 'warning' : 'error'}
            variant='outlined'
          >
            {commandDescription[command]}
          </ControllerAlert>
          {command !== DangrousNotificationType.Reboot && (
            <ControllerTextField
              label='Confirm command'
              name='confirm'
              helperText='Please type in the name of the command to confirm'
              value={confirmation}
              onChange={handleConfirmationChange}
              required
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={handleClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSubmit}
          size='medium'
          variant='contained'
          disabled={!enableSubmit}
        >
          Send command
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
